import { type AccountAuditEntry } from '@vello/api-client';
import { useT } from './provider';
import { useVocab } from './vocab';

/** The vocabularies a reason code of an account action can belong to (the action names which; the first that knows the code wins). */
const REASON_VOCABULARIES = ['reputation.suspension_reason', 'relationships.block_reason', 'account.phone_change_reason', 'account.rider_registration_reason'] as const;

/**
 * D577: the words of one line of the account-action history. The action is a declared code, the reason code is a code of the
 * vocabulary the action names (never the glued `CODE: note` text), and what an operator typed is shown as written.
 */
export function useAccountAuditText(): {
  readonly action: (e: AccountAuditEntry) => string;
  readonly detail: (e: AccountAuditEntry) => string | undefined;
} {
  const { lang } = useT();
  const { v, has } = useVocab();
  return {
    action: (e) => v('audit.account_action', e.action),
    detail: (e) => {
      const parts: string[] = [];
      if (e.reasonCode !== undefined) {
        const vocabulary = REASON_VOCABULARIES.find((id) => has(id, e.reasonCode));
        parts.push(v(vocabulary ?? REASON_VOCABULARIES[0], e.reasonCode));
      }
      if (e.fields !== undefined && e.fields.length > 0) parts.push(e.fields.map((f) => v('identity.profile_field', f)).join(', '));
      if (e.channel !== undefined) parts.push(v('notification.channel', e.channel));
      if (e.endsAt !== undefined) parts.push(new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }).format(new Date(e.endsAt)));
      if (e.note !== undefined && e.note !== '') parts.push(e.note);
      return parts.length > 0 ? parts.join(' · ') : undefined;
    },
  };
}
